import React, { useState, useEffect, useCallback } from 'react';
import { 
  consentManager, 
  CONSENT_TYPES, 
  CONSENT_VALUES,
  REGIONS
} from '../utils/consentMode';
import './ConsentBanner.css';

/**
 * Privacy-compliant consent banner with Google Consent Mode v2 integration
 * Supports GDPR, ePrivacy, CCPA compliance requirements
 */
const ConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [userRegion, setUserRegion] = useState(REGIONS.GLOBAL);
  const [preferences, setPreferences] = useState({
    [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
    [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
  });

  // Initialize consent manager and check if banner should be shown
  useEffect(() => {
    consentManager.init();
    
    const region = consentManager.getUserRegion();
    setUserRegion(region);
    
    // Show banner if user hasn't consented yet
    const hasConsented = consentManager.getConsentStatus().isValid;
    setIsVisible(!hasConsented);
    
    // Set initial preferences based on region defaults
    if (!hasConsented) {
      const defaultConsent = consentManager.getConsentStatus().preferences || 
        (region === REGIONS.CALIFORNIA ? {
          [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.GRANTED,
          [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
          [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
          [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
        } : {
          [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.DENIED,
          [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
          [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
          [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
        });
      
      setPreferences(defaultConsent);
    }
  }, []);

  // Handle accepting all cookies
  const handleAcceptAll = useCallback(() => {
    const allGranted = {
      [CONSENT_TYPES.ANALYTICS_STORAGE]: CONSENT_VALUES.GRANTED,
      [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.GRANTED,
      [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.GRANTED,
      [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.GRANTED
    };
    
    consentManager.updateConsent(allGranted);
    setIsVisible(false);
  }, []);

  // Handle accepting only essential cookies
  const handleAcceptEssential = useCallback(() => {
    const essentialOnly = {
      [CONSENT_TYPES.ANALYTICS_STORAGE]: userRegion === REGIONS.CALIFORNIA ? CONSENT_VALUES.GRANTED : CONSENT_VALUES.DENIED,
      [CONSENT_TYPES.AD_STORAGE]: CONSENT_VALUES.DENIED,
      [CONSENT_TYPES.AD_USER_DATA]: CONSENT_VALUES.DENIED,
      [CONSENT_TYPES.AD_PERSONALIZATION]: CONSENT_VALUES.DENIED
    };
    
    consentManager.updateConsent(essentialOnly);
    setIsVisible(false);
  }, [userRegion]);

  // Handle custom preferences
  const handleSavePreferences = useCallback(() => {
    consentManager.updateConsent(preferences);
    setIsVisible(false);
  }, [preferences]);

  // Toggle individual preference
  const togglePreference = useCallback((type) => {
    setPreferences(prev => ({
      ...prev,
      [type]: prev[type] === CONSENT_VALUES.GRANTED ? CONSENT_VALUES.DENIED : CONSENT_VALUES.GRANTED
    }));
  }, []);

  // Get region-specific text
  const getRegionText = () => {
    switch (userRegion) {
      case REGIONS.EU:
        return {
          title: 'Cookie Consent',
          description: 'We use cookies and similar technologies to improve your experience and measure site performance. By clicking "Accept All", you consent to our use of cookies as described in our Privacy Policy.',
          gdprNote: 'This site complies with GDPR requirements.',
          requiredByLaw: true
        };
      case REGIONS.UK:
        return {
          title: 'Cookie Consent',
          description: 'We use cookies to enhance your browsing experience and analyze site traffic. You can choose which cookies to accept.',
          gdprNote: 'This site complies with UK data protection laws.',
          requiredByLaw: true
        };
      case REGIONS.CALIFORNIA:
        return {
          title: 'Privacy Preferences',
          description: 'We respect your privacy. You can control how we use your data for advertising and analytics.',
          gdprNote: 'This site complies with CCPA requirements.',
          requiredByLaw: true
        };
      default:
        return {
          title: 'Cookie Preferences',
          description: 'We use cookies to improve your experience. You can customize your preferences below.',
          gdprNote: '',
          requiredByLaw: false
        };
    }
  };

  const regionText = getRegionText();

  if (!isVisible) return null;

  return (
    <div className="consent-banner" role="dialog" aria-label="Cookie consent" aria-modal="true">
      <div className="consent-banner__container">
        <div className="consent-banner__content">
          <div className="consent-banner__header">
            <h3 className="consent-banner__title">{regionText.title}</h3>
            {regionText.gdprNote && (
              <span className="consent-banner__compliance">{regionText.gdprNote}</span>
            )}
          </div>

          <p className="consent-banner__description">
            {regionText.description}
          </p>

          {showDetails && (
            <div className="consent-banner__details">
              <div className="consent-banner__preferences">
                <h4>Manage Your Preferences</h4>
                
                <div className="consent-preference">
                  <div className="consent-preference__info">
                    <strong>Essential Cookies</strong>
                    <p>Required for basic site functionality. Always enabled.</p>
                  </div>
                  <span className="consent-preference__status consent-preference__status--required">
                    Always Active
                  </span>
                </div>

                <div className="consent-preference">
                  <div className="consent-preference__info">
                    <strong>Analytics Cookies</strong>
                    <p>Help us understand how visitors interact with our website.</p>
                  </div>
                  <button
                    className={`consent-preference__toggle ${
                      preferences[CONSENT_TYPES.ANALYTICS_STORAGE] === CONSENT_VALUES.GRANTED ? 'active' : ''
                    }`}
                    onClick={() => togglePreference(CONSENT_TYPES.ANALYTICS_STORAGE)}
                    aria-label={`${preferences[CONSENT_TYPES.ANALYTICS_STORAGE] === CONSENT_VALUES.GRANTED ? 'Disable' : 'Enable'} analytics cookies`}
                  >
                    <span className="consent-preference__toggle-slider"></span>
                  </button>
                </div>

                <div className="consent-preference">
                  <div className="consent-preference__info">
                    <strong>Advertising Cookies</strong>
                    <p>Used to deliver personalized advertisements and measure campaign effectiveness.</p>
                  </div>
                  <button
                    className={`consent-preference__toggle ${
                      preferences[CONSENT_TYPES.AD_STORAGE] === CONSENT_VALUES.GRANTED ? 'active' : ''
                    }`}
                    onClick={() => togglePreference(CONSENT_TYPES.AD_STORAGE)}
                    aria-label={`${preferences[CONSENT_TYPES.AD_STORAGE] === CONSENT_VALUES.GRANTED ? 'Disable' : 'Enable'} advertising cookies`}
                  >
                    <span className="consent-preference__toggle-slider"></span>
                  </button>
                </div>

                <div className="consent-preference">
                  <div className="consent-preference__info">
                    <strong>Personalization</strong>
                    <p>Allow personalized content and user experience improvements.</p>
                  </div>
                  <button
                    className={`consent-preference__toggle ${
                      preferences[CONSENT_TYPES.AD_PERSONALIZATION] === CONSENT_VALUES.GRANTED ? 'active' : ''
                    }`}
                    onClick={() => togglePreference(CONSENT_TYPES.AD_PERSONALIZATION)}
                    aria-label={`${preferences[CONSENT_TYPES.AD_PERSONALIZATION] === CONSENT_VALUES.GRANTED ? 'Disable' : 'Enable'} personalization`}
                  >
                    <span className="consent-preference__toggle-slider"></span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="consent-banner__actions">
            <div className="consent-banner__actions-primary">
              <button
                className="consent-banner__button consent-banner__button--accept"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
              <button
                className="consent-banner__button consent-banner__button--essential"
                onClick={handleAcceptEssential}
              >
                {userRegion === REGIONS.CALIFORNIA ? 'Essential + Analytics' : 'Essential Only'}
              </button>
            </div>
            
            <div className="consent-banner__actions-secondary">
              <button
                className="consent-banner__button consent-banner__button--customize"
                onClick={() => setShowDetails(!showDetails)}
                aria-expanded={showDetails}
              >
                {showDetails ? 'Hide Details' : 'Customize'}
              </button>
              
              {showDetails && (
                <button
                  className="consent-banner__button consent-banner__button--save"
                  onClick={handleSavePreferences}
                >
                  Save Preferences
                </button>
              )}
            </div>
          </div>

          <div className="consent-banner__links">
            <a href="/privacy-policy" className="consent-banner__link">Privacy Policy</a>
            <a href="/cookie-policy" className="consent-banner__link">Cookie Policy</a>
            {regionText.requiredByLaw && (
              <a href="/privacy-rights" className="consent-banner__link">Your Privacy Rights</a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;